// Auto-generated from ClickHouse DDL (002_create_tables.sql) and event types (types.go)
// Last updated: 2026-03-19

export interface Column {
  name: string;
  type: string;
  description: string;
  nullable?: boolean;
}

export interface TableSchema {
  name: string;
  displayName: string;
  engine: string;
  domain: 'casino' | 'sport' | 'finance' | 'player' | 'reference' | 'aggregation';
  category: 'kafka' | 'bronze' | 'dimension' | 'gold';
  description: string;
  partitionBy?: string;
  orderBy?: string;
  ttl?: string;
  columns: Column[];
}

export interface EventType {
  name: string;
  domain: 'casino' | 'sport' | 'finance' | 'player';
  description: string;
  payloadFields: Column[];
}

// ── Event Types ──────────────────────────────────────────────

export const EVENT_TYPES: EventType[] = [
  // Casino
  {
    name: 'casino_bet',
    domain: 'casino',
    description: 'Bet placement in a casino game round',
    payloadFields: [
      { name: 'round_id', type: 'String', description: 'Unique game round identifier' },
      { name: 'transaction_id', type: 'Int64', description: 'Transaction reference from brand' },
      { name: 'brand', type: 'String', description: 'Game brand name (e.g. Pragmatic, Evolution)' },
      { name: 'game', type: 'String', description: 'Game identifier/slug' },
      { name: 'game_type', type: 'String', description: 'Game category: slots, live, table, crash, etc.' },
      { name: 'amount_real', type: 'Decimal128(18)', description: 'Real money wagered' },
      { name: 'amount_bonus', type: 'Decimal128(18)', description: 'Bonus money wagered' },
      { name: 'currency', type: 'LowCardinality(String)', description: 'Currency code (fiat ISO 4217 or crypto ticker)' },
      { name: 'player_balance', type: 'Decimal128(18)', description: 'Player balance after bet', nullable: true },
      { name: 'session_id', type: 'String', description: 'Player session identifier', nullable: true },
    ],
  },
  {
    name: 'casino_win',
    domain: 'casino',
    description: 'Win payout from a casino game round',
    payloadFields: [
      { name: 'round_id', type: 'String', description: 'Game round this win belongs to' },
      { name: 'transaction_id', type: 'Int64', description: 'Transaction reference' },
      { name: 'brand', type: 'String', description: 'Game brand name' },
      { name: 'game', type: 'String', description: 'Game identifier' },
      { name: 'game_type', type: 'String', description: 'Game category' },
      { name: 'amount_real', type: 'Decimal128(18)', description: 'Real money won' },
      { name: 'amount_bonus', type: 'Decimal128(18)', description: 'Bonus money won' },
      { name: 'currency', type: 'LowCardinality(String)', description: 'Currency code' },
      { name: 'player_balance', type: 'Decimal128(18)', description: 'Balance after win', nullable: true },
    ],
  },
  {
    name: 'casino_refund',
    domain: 'casino',
    description: 'Void or rollback of a casino bet (round cancelled)',
    payloadFields: [
      { name: 'round_id', type: 'String', description: 'Round being refunded' },
      { name: 'transaction_id', type: 'Int64', description: 'Original transaction reference' },
      { name: 'brand', type: 'String', description: 'Game brand' },
      { name: 'game', type: 'String', description: 'Game identifier' },
      { name: 'game_type', type: 'String', description: 'Game category' },
      { name: 'amount_real', type: 'Decimal128(18)', description: 'Real money refunded' },
      { name: 'amount_bonus', type: 'Decimal128(18)', description: 'Bonus money refunded' },
      { name: 'currency', type: 'LowCardinality(String)', description: 'Currency code' },
      { name: 'reason', type: 'String', description: 'Refund reason', nullable: true },
    ],
  },
  // Sportsbook
  {
    name: 'sport_place_bet',
    domain: 'sport',
    description: 'Sports bet placement (single, accumulator, system)',
    payloadFields: [
      { name: 'bet_id', type: 'String', description: 'Unique bet slip identifier' },
      { name: 'bet_type', type: 'String', description: 'Bet type: single, accumulator, system' },
      { name: 'sport', type: 'String', description: 'Sport name (football, basketball, etc.)' },
      { name: 'league', type: 'String', description: 'League/competition name' },
      { name: 'sport_event_id', type: 'String', description: 'Event/match identifier', nullable: true },
      { name: 'market', type: 'String', description: 'Betting market (1X2, Over/Under, etc.)' },
      { name: 'odds', type: 'Float64', description: 'Decimal odds at placement', nullable: true },
      { name: 'odds_format', type: 'String', description: 'Odds format: decimal, fractional, american' },
      { name: 'amount_real', type: 'Decimal128(18)', description: 'Real money staked' },
      { name: 'amount_bonus', type: 'Decimal128(18)', description: 'Bonus money staked' },
      { name: 'currency', type: 'LowCardinality(String)', description: 'Currency code' },
    ],
  },
  {
    name: 'sport_cancel_bet',
    domain: 'sport',
    description: 'Cancelled sports bet (voided by operator or system)',
    payloadFields: [
      { name: 'bet_id', type: 'String', description: 'Bet slip being cancelled' },
      { name: 'amount_real', type: 'Decimal128(18)', description: 'Real money returned' },
      { name: 'amount_bonus', type: 'Decimal128(18)', description: 'Bonus money returned' },
      { name: 'currency', type: 'LowCardinality(String)', description: 'Currency code' },
      { name: 'cancel_reason', type: 'String', description: 'Cancellation reason', nullable: true },
    ],
  },
  {
    name: 'sport_win',
    domain: 'sport',
    description: 'Sports bet settled as a win',
    payloadFields: [
      { name: 'bet_id', type: 'String', description: 'Winning bet identifier' },
      { name: 'odds', type: 'Float64', description: 'Final settled odds', nullable: true },
      { name: 'amount_real', type: 'Decimal128(18)', description: 'Real money payout' },
      { name: 'amount_bonus', type: 'Decimal128(18)', description: 'Bonus money payout' },
      { name: 'currency', type: 'LowCardinality(String)', description: 'Currency code' },
    ],
  },
  {
    name: 'sport_cashout_bet',
    domain: 'sport',
    description: 'Early cashout of a live sports bet',
    payloadFields: [
      { name: 'bet_id', type: 'String', description: 'Bet being cashed out' },
      { name: 'amount_real', type: 'Decimal128(18)', description: 'Cashout amount (real)' },
      { name: 'amount_bonus', type: 'Decimal128(18)', description: 'Cashout amount (bonus)' },
      { name: 'currency', type: 'LowCardinality(String)', description: 'Currency code' },
    ],
  },
  {
    name: 'sport_win_rollback',
    domain: 'sport',
    description: 'Reversal of a previously settled sports win',
    payloadFields: [
      { name: 'bet_id', type: 'String', description: 'Bet whose win is reversed' },
      { name: 'amount_real', type: 'Decimal128(18)', description: 'Real money clawed back' },
      { name: 'amount_bonus', type: 'Decimal128(18)', description: 'Bonus money clawed back' },
      { name: 'currency', type: 'LowCardinality(String)', description: 'Currency code' },
    ],
  },
  // Finance
  {
    name: 'deposit_completed',
    domain: 'finance',
    description: 'Deposit transaction successfully completed',
    payloadFields: [
      { name: 'movement_id', type: 'String', description: 'Unique financial movement ID' },
      { name: 'payment_method', type: 'String', description: 'Payment method (card, bank_transfer, crypto, etc.)' },
      { name: 'external_system', type: 'String', description: 'Payment processor name', nullable: true },
      { name: 'external_transaction_id', type: 'String', description: 'Processor transaction reference', nullable: true },
      { name: 'amount', type: 'Decimal128(18)', description: 'Deposit amount' },
      { name: 'currency', type: 'LowCardinality(String)', description: 'Currency code' },
      { name: 'is_first_deposit', type: 'UInt8', description: 'Flag: 1 if this is the player first deposit (FTD)', nullable: true },
    ],
  },
  {
    name: 'withdrawal_paid',
    domain: 'finance',
    description: 'Withdrawal request processed and paid out',
    payloadFields: [
      { name: 'movement_id', type: 'String', description: 'Financial movement ID' },
      { name: 'payment_method', type: 'String', description: 'Payout method' },
      { name: 'amount', type: 'Decimal128(18)', description: 'Withdrawal amount' },
      { name: 'currency', type: 'LowCardinality(String)', description: 'Currency code' },
    ],
  },
  {
    name: 'withdrawal_cancelled',
    domain: 'finance',
    description: 'Withdrawal request cancelled (by player or operator)',
    payloadFields: [
      { name: 'movement_id', type: 'String', description: 'Movement being cancelled' },
      { name: 'amount', type: 'Decimal128(18)', description: 'Amount returned to balance' },
      { name: 'currency', type: 'LowCardinality(String)', description: 'Currency code' },
      { name: 'reason', type: 'String', description: 'Cancellation reason', nullable: true },
    ],
  },
  {
    name: 'bonus_emitted',
    domain: 'finance',
    description: 'Bonus issued to a player account',
    payloadFields: [
      { name: 'bonus_id', type: 'String', description: 'Unique bonus instance ID' },
      { name: 'bonus_name', type: 'String', description: 'Bonus display name', nullable: true },
      { name: 'bonus_type', type: 'String', description: 'Type: deposit_bonus, free_spin, free_bet, cashback' },
      { name: 'promo_id', type: 'String', description: 'Promotion/campaign ID', nullable: true },
      { name: 'amount', type: 'Decimal128(18)', description: 'Bonus value' },
      { name: 'currency', type: 'LowCardinality(String)', description: 'Currency code' },
      { name: 'wagering_requirement', type: 'Float64', description: 'Wagering multiplier (e.g. 35x)', nullable: true },
    ],
  },
  {
    name: 'bonus_redeemed',
    domain: 'finance',
    description: 'Bonus wagering requirement completed and funds released',
    payloadFields: [
      { name: 'bonus_id', type: 'String', description: 'Bonus instance redeemed' },
      { name: 'bonus_type', type: 'String', description: 'Bonus type' },
      { name: 'amount', type: 'Decimal128(18)', description: 'Amount converted to real balance' },
      { name: 'currency', type: 'LowCardinality(String)', description: 'Currency code' },
    ],
  },
  {
    name: 'cashback_completed',
    domain: 'finance',
    description: 'Cashback payout credited to player',
    payloadFields: [
      { name: 'amount', type: 'Decimal128(18)', description: 'Cashback amount' },
      { name: 'currency', type: 'LowCardinality(String)', description: 'Currency code' },
      { name: 'product', type: 'String', description: 'Product: casino, sportsbook, all', nullable: true },
    ],
  },
  // Player
  {
    name: 'create_player',
    domain: 'player',
    description: 'New player account registration',
    payloadFields: [
      { name: 'username', type: 'String', description: 'Player username' },
      { name: 'country_code', type: 'String', description: 'ISO 3166-1 alpha-2 country' },
      { name: 'language_code', type: 'String', description: 'ISO 639-1 language', nullable: true },
      { name: 'currency_code', type: 'LowCardinality(String)', description: 'Player default currency' },
      { name: 'email', type: 'String', description: 'Email address', nullable: true },
      { name: 'birth_date', type: 'Date', description: 'Date of birth', nullable: true },
      { name: 'affiliate_id', type: 'String', description: 'Affiliate/tracker ID', nullable: true },
      { name: 'gender', type: 'String', description: 'Gender: M, F, O', nullable: true },
      { name: 'device_type', type: 'String', description: 'Registration device: desktop, mobile, tablet', nullable: true },
    ],
  },
  {
    name: 'player_login',
    domain: 'player',
    description: 'Player session login event',
    payloadFields: [
      { name: 'ip_address', type: 'String', description: 'Client IP address' },
      { name: 'user_agent', type: 'String', description: 'Browser user agent', nullable: true },
      { name: 'device_type', type: 'String', description: 'Device type' },
      { name: 'success', type: 'UInt8', description: 'Login success flag (1=ok, 0=failed)' },
      { name: 'session_id', type: 'String', description: 'New session identifier', nullable: true },
    ],
  },
  {
    name: 'player_status_changed',
    domain: 'player',
    description: 'Player account status change (active, suspended, closed, etc.)',
    payloadFields: [
      { name: 'old_status', type: 'String', description: 'Previous status' },
      { name: 'new_status', type: 'String', description: 'New status' },
      { name: 'reason', type: 'String', description: 'Change reason', nullable: true },
      { name: 'author_id', type: 'String', description: 'Operator who made the change', nullable: true },
    ],
  },
  {
    name: 'player_email_confirmed',
    domain: 'player',
    description: 'Player email address verified',
    payloadFields: [
      { name: 'email', type: 'String', description: 'Confirmed email address' },
      { name: 'is_verified', type: 'UInt8', description: 'Verification flag' },
    ],
  },
  {
    name: 'player_kyc_verified',
    domain: 'player',
    description: 'Player KYC verification completed',
    payloadFields: [
      { name: 'is_verified', type: 'UInt8', description: 'KYC passed flag' },
      { name: 'author_id', type: 'String', description: 'Verifier ID', nullable: true },
      { name: 'author_username', type: 'String', description: 'Verifier name', nullable: true },
    ],
  },
];

// ── Domain colors ────────────────────────────────────────────

export const DOMAIN_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  casino: { bg: 'bg-purple-500/10', text: 'text-purple-400', border: 'border-purple-500/30' },
  sport: { bg: 'bg-green-500/10', text: 'text-green-400', border: 'border-green-500/30' },
  finance: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/30' },
  player: { bg: 'bg-accent-500/10', text: 'text-accent-400', border: 'border-accent-500/30' },
  reference: { bg: 'bg-slate-500/10', text: 'text-slate-400', border: 'border-slate-500/30' },
  aggregation: { bg: 'bg-cyan-500/10', text: 'text-cyan-400', border: 'border-cyan-500/30' },
};

// ── ClickHouse Tables ────────────────────────────────────────

const COMMON_ENVELOPE: Column[] = [
  { name: 'event_id', type: 'String', description: 'Unique event UUID' },
  { name: 'event_date', type: 'DateTime64(3, \'UTC\')', description: 'Event timestamp (UTC, ms precision)' },
  { name: 'site_id', type: 'UInt32', description: 'Site identifier' },
  { name: 'player_id', type: 'UInt64', description: 'Player account identifier' },
  { name: 'event_type', type: 'LowCardinality(String)', description: 'Event type constant' },
  { name: 'schema_version', type: 'LowCardinality(String)', description: 'Schema version (e.g. v1)' },
  { name: 'produced_at', type: 'DateTime64(3, \'UTC\')', description: 'Timestamp when event was produced' },
  { name: 'producer_app', type: 'LowCardinality(String)', description: 'Source application identifier' },
];

export const TABLES: TableSchema[] = [
  // ── Kafka Engine ───────────────────────────────────────────
  {
    name: 'events_casino_kafka',
    displayName: 'Casino Kafka Engine',
    engine: 'Kafka',
    domain: 'casino',
    category: 'kafka',
    description: 'Kafka consumer table for casino events from bsr.casino.v1 topic',
    columns: [
      ...COMMON_ENVELOPE,
      { name: 'round_id', type: 'String', description: 'Game round ID' },
      { name: 'transaction_id', type: 'Int64', description: 'Brand transaction reference' },
      { name: 'brand', type: 'LowCardinality(String)', description: 'Game brand' },
      { name: 'game', type: 'String', description: 'Game slug' },
      { name: 'game_type', type: 'LowCardinality(String)', description: 'Game category' },
      { name: 'amount_real', type: 'Decimal128(18)', description: 'Real money amount' },
      { name: 'amount_bonus', type: 'Decimal128(18)', description: 'Bonus money amount' },
      { name: 'currency', type: 'LowCardinality(String)', description: 'Currency code (fiat or crypto)' },
      { name: 'player_balance', type: 'Nullable(Decimal128(18))', description: 'Balance after event', nullable: true },
      { name: 'session_id', type: 'Nullable(String)', description: 'Session ID', nullable: true },
      { name: 'reason', type: 'Nullable(String)', description: 'Refund reason (for casino_refund)', nullable: true },
    ],
  },
  {
    name: 'events_sport_kafka',
    displayName: 'Sport Kafka Engine',
    engine: 'Kafka',
    domain: 'sport',
    category: 'kafka',
    description: 'Kafka consumer table for sportsbook events from bsr.sport.v1 topic',
    columns: [
      ...COMMON_ENVELOPE,
      { name: 'movement_id', type: 'String', description: 'Financial movement ID' },
      { name: 'bet_id', type: 'String', description: 'Bet slip identifier' },
      { name: 'bet_type', type: 'LowCardinality(String)', description: 'single, accumulator, system' },
      { name: 'sport', type: 'LowCardinality(String)', description: 'Sport name' },
      { name: 'league', type: 'String', description: 'League/competition' },
      { name: 'sport_event_id', type: 'Nullable(String)', description: 'Match/event ID', nullable: true },
      { name: 'market', type: 'Nullable(String)', description: 'Betting market', nullable: true },
      { name: 'odds', type: 'Nullable(Float64)', description: 'Decimal odds', nullable: true },
      { name: 'odds_format', type: 'LowCardinality(String)', description: 'Odds format' },
      { name: 'amount_real', type: 'Decimal128(18)', description: 'Real money' },
      { name: 'amount_bonus', type: 'Decimal128(18)', description: 'Bonus money' },
      { name: 'currency', type: 'LowCardinality(String)', description: 'Currency' },
      { name: 'player_balance', type: 'Nullable(Decimal128(18))', description: 'Balance after', nullable: true },
      { name: 'cancel_reason', type: 'Nullable(String)', description: 'Cancellation reason', nullable: true },
    ],
  },
  {
    name: 'events_finance_kafka',
    displayName: 'Finance Kafka Engine',
    engine: 'Kafka',
    domain: 'finance',
    category: 'kafka',
    description: 'Kafka consumer for deposit, withdrawal, bonus, and cashback events from bsr.finance.v1',
    columns: [
      ...COMMON_ENVELOPE,
      { name: 'movement_id', type: 'Nullable(String)', description: 'Financial movement ID', nullable: true },
      { name: 'payment_method', type: 'LowCardinality(String)', description: 'Payment method' },
      { name: 'external_system', type: 'Nullable(String)', description: 'Payment processor', nullable: true },
      { name: 'external_transaction_id', type: 'Nullable(String)', description: 'Processor reference', nullable: true },
      { name: 'amount', type: 'Decimal128(18)', description: 'Transaction amount' },
      { name: 'currency', type: 'LowCardinality(String)', description: 'Currency' },
      { name: 'player_balance', type: 'Nullable(Decimal128(18))', description: 'Balance after', nullable: true },
      { name: 'is_first_deposit', type: 'Nullable(UInt8)', description: 'FTD flag', nullable: true },
      { name: 'bonus_code', type: 'Nullable(String)', description: 'Bonus code used', nullable: true },
      { name: 'reason', type: 'Nullable(String)', description: 'Reason', nullable: true },
      { name: 'bonus_id', type: 'Nullable(String)', description: 'Bonus instance ID', nullable: true },
      { name: 'bonus_name', type: 'Nullable(String)', description: 'Bonus name', nullable: true },
      { name: 'bonus_type', type: 'LowCardinality(String)', description: 'Bonus type' },
      { name: 'promo_id', type: 'Nullable(String)', description: 'Promotion ID', nullable: true },
      { name: 'wagering_requirement', type: 'Nullable(Float64)', description: 'Wagering multiplier', nullable: true },
      { name: 'product', type: 'LowCardinality(String)', description: 'Product: casino, sportsbook' },
    ],
  },
  {
    name: 'events_player_kafka',
    displayName: 'Player Kafka Engine',
    engine: 'Kafka',
    domain: 'player',
    category: 'kafka',
    description: 'Kafka consumer for player lifecycle events from bsr.player.v1',
    columns: [
      ...COMMON_ENVELOPE,
      { name: 'username', type: 'Nullable(String)', description: 'Username', nullable: true },
      { name: 'status', type: 'LowCardinality(String)', description: 'Account status' },
      { name: 'country_code', type: 'LowCardinality(String)', description: 'Country ISO alpha-2' },
      { name: 'language_code', type: 'LowCardinality(String)', description: 'Language ISO 639-1' },
      { name: 'currency_code', type: 'LowCardinality(String)', description: 'Default currency' },
      { name: 'email', type: 'Nullable(String)', description: 'Email', nullable: true },
      { name: 'phone', type: 'Nullable(String)', description: 'Phone', nullable: true },
      { name: 'first_name', type: 'Nullable(String)', description: 'First name', nullable: true },
      { name: 'last_name', type: 'Nullable(String)', description: 'Last name', nullable: true },
      { name: 'birth_date', type: 'Nullable(Date)', description: 'Date of birth', nullable: true },
      { name: 'affiliate_id', type: 'Nullable(String)', description: 'Affiliate tracker', nullable: true },
      { name: 'gender', type: 'Nullable(String)', description: 'Gender', nullable: true },
      { name: 'ip_address', type: 'Nullable(String)', description: 'IP address', nullable: true },
      { name: 'device_type', type: 'LowCardinality(String)', description: 'Device type' },
      { name: 'session_id', type: 'Nullable(String)', description: 'Session', nullable: true },
      { name: 'success', type: 'Nullable(UInt8)', description: 'Login success flag', nullable: true },
      { name: 'old_status', type: 'Nullable(String)', description: 'Previous status', nullable: true },
      { name: 'new_status', type: 'Nullable(String)', description: 'New status', nullable: true },
      { name: 'is_verified', type: 'Nullable(UInt8)', description: 'Verification flag', nullable: true },
    ],
  },

  // ── Bronze Tables ──────────────────────────────────────────
  {
    name: 'events_casino_raw',
    displayName: 'Casino Bronze',
    engine: 'ReplacingMergeTree(_inserted_at)',
    domain: 'casino',
    category: 'bronze',
    description: 'Raw casino events — bets, wins, refunds. Deduplicated by event_id within partition.',
    partitionBy: 'toYYYYMM(event_date)',
    orderBy: '(site_id, event_date, event_id)',
    ttl: '24 months',
    columns: [
      ...COMMON_ENVELOPE,
      { name: 'round_id', type: 'String', description: 'Game round ID' },
      { name: 'transaction_id', type: 'UInt64', description: 'Brand transaction reference' },
      { name: 'brand', type: 'LowCardinality(String)', description: 'Game brand name' },
      { name: 'game', type: 'String', description: 'Game identifier' },
      { name: 'game_type', type: 'LowCardinality(String)', description: 'Category: slots, live, table, crash' },
      { name: 'amount_real', type: 'Decimal128(18)', description: 'Real money amount' },
      { name: 'amount_bonus', type: 'Decimal128(18)', description: 'Bonus money amount' },
      { name: 'currency', type: 'LowCardinality(String)', description: 'Currency code (fiat or crypto)' },
      { name: 'player_balance', type: 'Nullable(Decimal128(18))', description: 'Balance after event', nullable: true },
      { name: 'session_id', type: 'Nullable(String)', description: 'Session ID', nullable: true },
      { name: 'reason', type: 'Nullable(String)', description: 'Refund reason', nullable: true },
      { name: '_inserted_at', type: 'DateTime64(3)', description: 'ClickHouse insertion time (dedup version)' },
    ],
  },
  {
    name: 'events_sport_raw',
    displayName: 'Sport Bronze',
    engine: 'ReplacingMergeTree(_inserted_at)',
    domain: 'sport',
    category: 'bronze',
    description: 'Raw sportsbook events — bets, wins, cancellations, cashouts, rollbacks.',
    partitionBy: 'toYYYYMM(event_date)',
    orderBy: '(site_id, event_date, event_id)',
    ttl: '24 months',
    columns: [
      ...COMMON_ENVELOPE,
      { name: 'movement_id', type: 'Nullable(UInt64)', description: 'Financial movement', nullable: true },
      { name: 'bet_id', type: 'String', description: 'Bet slip ID' },
      { name: 'bet_type', type: 'LowCardinality(String)', description: 'Bet type' },
      { name: 'sport', type: 'LowCardinality(String)', description: 'Sport name' },
      { name: 'league', type: 'String', description: 'League' },
      { name: 'sport_event_id', type: 'Nullable(String)', description: 'Event ID', nullable: true },
      { name: 'market', type: 'Nullable(String)', description: 'Market', nullable: true },
      { name: 'odds', type: 'Nullable(Float64)', description: 'Odds', nullable: true },
      { name: 'odds_format', type: 'LowCardinality(String)', description: 'Odds format' },
      { name: 'amount_real', type: 'Decimal128(18)', description: 'Real money' },
      { name: 'amount_bonus', type: 'Decimal128(18)', description: 'Bonus money' },
      { name: 'currency', type: 'LowCardinality(String)', description: 'Currency' },
      { name: 'player_balance', type: 'Nullable(Decimal128(18))', description: 'Balance', nullable: true },
      { name: 'cancel_reason', type: 'Nullable(String)', description: 'Cancel reason', nullable: true },
      { name: '_inserted_at', type: 'DateTime64(3)', description: 'Insertion timestamp' },
    ],
  },
  {
    name: 'events_finance_raw',
    displayName: 'Finance Bronze',
    engine: 'ReplacingMergeTree(_inserted_at)',
    domain: 'finance',
    category: 'bronze',
    description: 'Raw finance events — deposits, withdrawals, bonuses, cashbacks.',
    partitionBy: 'toYYYYMM(event_date)',
    orderBy: '(site_id, event_date, event_id)',
    ttl: '24 months',
    columns: [
      ...COMMON_ENVELOPE,
      { name: 'movement_id', type: 'Nullable(UInt64)', description: 'Movement ID', nullable: true },
      { name: 'payment_method', type: 'LowCardinality(String)', description: 'Payment method' },
      { name: 'external_system', type: 'Nullable(String)', description: 'Processor', nullable: true },
      { name: 'external_transaction_id', type: 'Nullable(String)', description: 'Processor ref', nullable: true },
      { name: 'amount', type: 'Decimal128(18)', description: 'Amount' },
      { name: 'currency', type: 'LowCardinality(String)', description: 'Currency' },
      { name: 'player_balance', type: 'Nullable(Decimal128(18))', description: 'Balance', nullable: true },
      { name: 'is_first_deposit', type: 'Nullable(UInt8)', description: 'FTD flag', nullable: true },
      { name: 'bonus_code', type: 'Nullable(String)', description: 'Bonus code', nullable: true },
      { name: 'reason', type: 'Nullable(String)', description: 'Reason', nullable: true },
      { name: 'bonus_id', type: 'Nullable(UInt64)', description: 'Bonus ID', nullable: true },
      { name: 'bonus_name', type: 'Nullable(String)', description: 'Bonus name', nullable: true },
      { name: 'bonus_type', type: 'LowCardinality(String)', description: 'Bonus type' },
      { name: 'promo_id', type: 'Nullable(UInt64)', description: 'Promotion ID', nullable: true },
      { name: 'wagering_requirement', type: 'Nullable(UInt16)', description: 'Wagering req', nullable: true },
      { name: 'product', type: 'LowCardinality(String)', description: 'Product' },
      { name: '_inserted_at', type: 'DateTime64(3)', description: 'Insertion timestamp' },
    ],
  },
  {
    name: 'events_player_raw',
    displayName: 'Player Bronze',
    engine: 'ReplacingMergeTree(_inserted_at)',
    domain: 'player',
    category: 'bronze',
    description: 'Raw player lifecycle events — registration, login, status changes, KYC.',
    partitionBy: 'toYYYYMM(event_date)',
    orderBy: '(site_id, event_date, event_id)',
    ttl: '24 months',
    columns: [
      ...COMMON_ENVELOPE,
      { name: 'username', type: 'Nullable(String)', description: 'Username', nullable: true },
      { name: 'status', type: 'LowCardinality(String)', description: 'Status' },
      { name: 'country_code', type: 'LowCardinality(String)', description: 'Country' },
      { name: 'language_code', type: 'LowCardinality(String)', description: 'Language' },
      { name: 'currency_code', type: 'LowCardinality(String)', description: 'Currency' },
      { name: 'email', type: 'Nullable(String)', description: 'Email', nullable: true },
      { name: 'birth_date', type: 'Nullable(Date)', description: 'Birth date', nullable: true },
      { name: 'affiliate_id', type: 'Nullable(String)', description: 'Affiliate', nullable: true },
      { name: 'gender', type: 'Nullable(String)', description: 'Gender', nullable: true },
      { name: 'ip_address', type: 'Nullable(String)', description: 'IP', nullable: true },
      { name: 'device_type', type: 'LowCardinality(String)', description: 'Device' },
      { name: 'session_id', type: 'Nullable(String)', description: 'Session', nullable: true },
      { name: 'success', type: 'Nullable(UInt8)', description: 'Success flag', nullable: true },
      { name: 'old_status', type: 'Nullable(String)', description: 'Old status', nullable: true },
      { name: 'new_status', type: 'Nullable(String)', description: 'New status', nullable: true },
      { name: 'is_verified', type: 'Nullable(UInt8)', description: 'Verified', nullable: true },
      { name: '_inserted_at', type: 'DateTime64(3)', description: 'Insertion timestamp' },
    ],
  },

  // ── Dimension Tables ───────────────────────────────────────
  {
    name: 'dim_site', displayName: 'Site', engine: 'ReplacingMergeTree(updated_at)',
    domain: 'reference', category: 'dimension',
    description: 'Site master data — one row per site.',
    orderBy: '(site_id)',
    columns: [
      { name: 'site_id', type: 'UInt32', description: 'Unique site ID' },
      { name: 'site_name', type: 'String', description: 'Site display name' },
      { name: 'country', type: 'LowCardinality(String)', description: 'HQ country' },
      { name: 'timezone', type: 'String', description: 'Business timezone (IANA)' },
      { name: 'currency_base', type: 'LowCardinality(String)', description: 'Base reporting currency' },
      { name: 'license_type', type: 'LowCardinality(String)', description: 'License jurisdiction' },
      { name: 'is_active', type: 'UInt8', description: 'Active flag' },
      { name: 'created_at', type: 'DateTime', description: 'Creation date' },
      { name: 'updated_at', type: 'DateTime', description: 'Last update' },
    ],
  },
  {
    name: 'dim_player', displayName: 'Player', engine: 'ReplacingMergeTree(updated_at)',
    domain: 'player', category: 'dimension',
    description: 'Player master data — demographics, registration, KYC status.',
    orderBy: '(site_id, player_id)',
    columns: [
      { name: 'site_id', type: 'UInt32', description: 'Site' },
      { name: 'player_id', type: 'UInt64', description: 'Player ID' },
      { name: 'username', type: 'String', description: 'Username' },
      { name: 'country_code', type: 'LowCardinality(String)', description: 'Country' },
      { name: 'language_code', type: 'LowCardinality(String)', description: 'Language' },
      { name: 'currency_code', type: 'LowCardinality(String)', description: 'Default currency' },
      { name: 'registration_date', type: 'DateTime', description: 'Registration timestamp' },
      { name: 'status', type: 'LowCardinality(String)', description: 'Account status' },
      { name: 'kyc_level', type: 'UInt8', description: 'KYC verification level' },
      { name: 'affiliate_id', type: 'Nullable(String)', description: 'Affiliate tracker', nullable: true },
      { name: 'gender', type: 'Nullable(String)', description: 'Gender', nullable: true },
      { name: 'birth_date', type: 'Nullable(Date)', description: 'Birth date', nullable: true },
      { name: 'first_deposit_date', type: 'Nullable(DateTime)', description: 'FTD date', nullable: true },
      { name: 'first_deposit_amount', type: 'Nullable(Decimal128(18))', description: 'FTD amount', nullable: true },
      { name: 'updated_at', type: 'DateTime', description: 'Last update' },
    ],
  },
  {
    name: 'dim_game', displayName: 'Game', engine: 'ReplacingMergeTree(updated_at)',
    domain: 'casino', category: 'dimension',
    description: 'Casino game catalog — name, type, brand, RTP.',
    orderBy: '(game_id)',
    columns: [
      { name: 'game_id', type: 'UInt32', description: 'Game ID' },
      { name: 'game_name', type: 'String', description: 'Display name' },
      { name: 'game_slug', type: 'String', description: 'URL slug' },
      { name: 'game_type', type: 'LowCardinality(String)', description: 'Category' },
      { name: 'brand_id', type: 'UInt32', description: 'Brand FK' },
      { name: 'rtp_theoretical', type: 'Float32', description: 'Theoretical RTP (e.g. 96.5)' },
      { name: 'volatility', type: 'LowCardinality(String)', description: 'low, medium, high' },
      { name: 'release_date', type: 'Nullable(Date)', description: 'Release date', nullable: true },
      { name: 'is_active', type: 'UInt8', description: 'Active flag' },
      { name: 'updated_at', type: 'DateTime', description: 'Last update' },
    ],
  },
  {
    name: 'dim_brand', displayName: 'Brand', engine: 'ReplacingMergeTree(updated_at)',
    domain: 'casino', category: 'dimension',
    description: 'Game brand info — fee tiers, SLA, contact.',
    orderBy: '(site_id, brand_id)',
    columns: [
      { name: 'site_id', type: 'UInt32', description: 'Site' },
      { name: 'brand_id', type: 'UInt32', description: 'Brand ID' },
      { name: 'brand_name', type: 'String', description: 'Name' },
      { name: 'country', type: 'LowCardinality(String)', description: 'Country' },
      { name: 'fee_tier', type: 'LowCardinality(String)', description: 'Fee tier name' },
      { name: 'fee_percentage', type: 'Float32', description: 'Fee %' },
      { name: 'sla_uptime', type: 'Float32', description: 'SLA uptime %' },
      { name: 'is_active', type: 'UInt8', description: 'Active' },
      { name: 'updated_at', type: 'DateTime', description: 'Last update' },
    ],
  },
  {
    name: 'dim_currency', displayName: 'Currency', engine: 'ReplacingMergeTree(updated_at)',
    domain: 'reference', category: 'dimension',
    description: 'ISO 4217 currency reference data.',
    orderBy: '(currency_code)',
    columns: [
      { name: 'currency_code', type: 'LowCardinality(String)', description: 'Currency code (fiat or crypto)' },
      { name: 'currency_name', type: 'String', description: 'Full name' },
      { name: 'symbol', type: 'String', description: 'Symbol (e.g. $, EUR)' },
      { name: 'decimal_places', type: 'UInt8', description: 'Minor units' },
      { name: 'updated_at', type: 'DateTime', description: 'Last update' },
    ],
  },
  {
    name: 'dim_exchange_rate', displayName: 'Exchange Rate', engine: 'ReplacingMergeTree(loaded_at)',
    domain: 'reference', category: 'dimension',
    description: 'Daily exchange rates for EUR/USD currency conversion.',
    partitionBy: 'toYYYYMM(date)',
    orderBy: '(date, currency_from, currency_to)',
    columns: [
      { name: 'date', type: 'Date', description: 'Rate date' },
      { name: 'currency_from', type: 'String', description: 'Source currency' },
      { name: 'currency_to', type: 'String', description: 'Target currency' },
      { name: 'rate', type: 'Float64', description: 'Conversion rate' },
      { name: 'source', type: 'LowCardinality(String)', description: 'Rate source (fixer.io)' },
      { name: 'is_fallback', type: 'UInt8', description: 'Fallback rate flag' },
      { name: 'loaded_at', type: 'DateTime', description: 'Load timestamp' },
    ],
  },
  {
    name: 'dim_payment_method', displayName: 'Payment Method', engine: 'ReplacingMergeTree(updated_at)',
    domain: 'finance', category: 'dimension',
    description: 'Payment method catalog — card, bank, crypto, e-wallet, voucher.',
    orderBy: '(payment_method_id)',
    columns: [
      { name: 'payment_method_id', type: 'UInt32', description: 'Method ID' },
      { name: 'payment_method_name', type: 'String', description: 'Display name' },
      { name: 'category', type: 'LowCardinality(String)', description: 'Category' },
      { name: 'processor', type: 'String', description: 'Processor name' },
      { name: 'fee_percentage', type: 'Float32', description: 'Fee %' },
      { name: 'updated_at', type: 'DateTime', description: 'Last update' },
    ],
  },
  {
    name: 'dim_bonus_type', displayName: 'Bonus Type', engine: 'ReplacingMergeTree(updated_at)',
    domain: 'finance', category: 'dimension',
    description: 'Bonus type definitions and cost models.',
    orderBy: '(bonus_type_id)',
    columns: [
      { name: 'bonus_type_id', type: 'UInt32', description: 'Bonus type ID' },
      { name: 'bonus_type_name', type: 'String', description: 'Name' },
      { name: 'is_deposit_bonus', type: 'UInt8', description: 'Deposit bonus flag' },
      { name: 'is_free_spin', type: 'UInt8', description: 'Free spin flag' },
      { name: 'is_free_bet', type: 'UInt8', description: 'Free bet flag' },
      { name: 'cost_model', type: 'LowCardinality(String)', description: 'Cost model' },
      { name: 'updated_at', type: 'DateTime', description: 'Last update' },
    ],
  },

  // ── Gold Aggregation Tables ────────────────────────────────
  {
    name: 'agg_casino_daily', displayName: 'Casino Daily', engine: 'AggregatingMergeTree',
    domain: 'casino', category: 'gold',
    description: 'Daily casino aggregations — GGR, turnover, bet/win/void counts per site, brand, game.',
    partitionBy: 'toYYYYMM(date)',
    orderBy: '(site_id, date, brand_id, game_id, currency)',
    columns: [
      { name: 'site_id', type: 'UInt32', description: 'Site' },
      { name: 'date', type: 'Date', description: 'Business day (CET)' },
      { name: 'brand_id', type: 'UInt32', description: 'Brand' },
      { name: 'game_id', type: 'UInt32', description: 'Game' },
      { name: 'currency', type: 'LowCardinality(String)', description: 'Native currency' },
      { name: 'game_type', type: 'LowCardinality(String)', description: 'Game category' },
      { name: 'bet_amount_*', type: 'AggregateFunction(sum)', description: 'Bet amounts (real/bonus/total × native/EUR/USD)' },
      { name: 'win_amount_*', type: 'AggregateFunction(sum)', description: 'Win amounts (same variants)' },
      { name: 'void_amount_*', type: 'AggregateFunction(sum)', description: 'Void amounts' },
      { name: 'ggr_*', type: 'AggregateFunction(sum)', description: 'GGR = bets - wins - refunds (× currencies)' },
      { name: 'turnover_*', type: 'AggregateFunction(sum)', description: 'Turnover = total bets' },
      { name: 'bet_count_*', type: 'AggregateFunction(sum, UInt64)', description: 'Bet counts (real/bonus)' },
      { name: 'unique_players', type: 'AggregateFunction(uniq, UInt64)', description: 'Distinct active players' },
      { name: 'unique_sessions', type: 'AggregateFunction(uniq, String)', description: 'Distinct sessions' },
    ],
  },
  {
    name: 'agg_sportsbook_daily', displayName: 'Sportsbook Daily', engine: 'AggregatingMergeTree',
    domain: 'sport', category: 'gold',
    description: 'Daily sportsbook aggregations — bets, wins, GGR per sport, league, event.',
    partitionBy: 'toYYYYMM(date)',
    orderBy: '(site_id, date, sport_id, event_id, currency)',
    columns: [
      { name: 'site_id', type: 'UInt32', description: 'Site' },
      { name: 'date', type: 'Date', description: 'Business day (CET)' },
      { name: 'sport_id', type: 'UInt32', description: 'Sport' },
      { name: 'league_id', type: 'UInt32', description: 'League' },
      { name: 'event_id', type: 'UInt64', description: 'Event' },
      { name: 'currency', type: 'LowCardinality(String)', description: 'Currency' },
      { name: 'bet_amount_*', type: 'AggregateFunction(sum)', description: 'Bet amounts' },
      { name: 'win_amount_*', type: 'AggregateFunction(sum)', description: 'Win amounts' },
      { name: 'ggr_*', type: 'AggregateFunction(sum)', description: 'GGR' },
      { name: 'unique_players', type: 'AggregateFunction(uniq, UInt64)', description: 'Distinct bettors' },
    ],
  },
  {
    name: 'agg_deposit_daily', displayName: 'Deposit Daily', engine: 'AggregatingMergeTree',
    domain: 'finance', category: 'gold',
    description: 'Daily deposit aggregations by payment method.',
    partitionBy: 'toYYYYMM(date)',
    orderBy: '(site_id, date, payment_method_id, currency)',
    columns: [
      { name: 'site_id', type: 'UInt32', description: 'Site' },
      { name: 'date', type: 'Date', description: 'Business day (CET)' },
      { name: 'payment_method_id', type: 'UInt32', description: 'Payment method' },
      { name: 'currency', type: 'LowCardinality(String)', description: 'Currency' },
      { name: 'deposit_amount_*', type: 'AggregateFunction(sum)', description: 'Deposit amounts (native/EUR/USD)' },
      { name: 'ftd_amount_*', type: 'AggregateFunction(sum)', description: 'First-time deposit amounts' },
      { name: 'unique_depositors', type: 'AggregateFunction(uniq, UInt64)', description: 'Distinct depositors' },
    ],
  },
  {
    name: 'agg_withdrawal_daily', displayName: 'Withdrawal Daily', engine: 'AggregatingMergeTree',
    domain: 'finance', category: 'gold',
    description: 'Daily withdrawal aggregations by payment method.',
    partitionBy: 'toYYYYMM(date)',
    orderBy: '(site_id, date, payment_method_id, currency)',
    columns: [
      { name: 'site_id', type: 'UInt32', description: 'Site' },
      { name: 'date', type: 'Date', description: 'Business day' },
      { name: 'payment_method_id', type: 'UInt32', description: 'Method' },
      { name: 'currency', type: 'LowCardinality(String)', description: 'Currency' },
      { name: 'withdrawal_amount_*', type: 'AggregateFunction(sum)', description: 'Withdrawal amounts' },
      { name: 'cancelled_amount_*', type: 'AggregateFunction(sum)', description: 'Cancelled amounts' },
    ],
  },
  {
    name: 'agg_bonus_daily', displayName: 'Bonus Daily', engine: 'AggregatingMergeTree',
    domain: 'finance', category: 'gold',
    description: 'Daily bonus aggregations by type and promotion.',
    partitionBy: 'toYYYYMM(date)',
    orderBy: '(site_id, date, bonus_type_id)',
    columns: [
      { name: 'site_id', type: 'UInt32', description: 'Site' },
      { name: 'date', type: 'Date', description: 'Business day' },
      { name: 'bonus_type_id', type: 'UInt32', description: 'Bonus type' },
      { name: 'promo_id', type: 'UInt32', description: 'Promotion' },
      { name: 'bonus_rw_amount_*', type: 'AggregateFunction(sum)', description: 'Bonus reward amounts' },
      { name: 'freebet_amount_*', type: 'AggregateFunction(sum)', description: 'Free bet amounts' },
      { name: 'cashback_amount_*', type: 'AggregateFunction(sum)', description: 'Cashback amounts' },
    ],
  },
  {
    name: 'agg_player_activity_daily', displayName: 'Player Activity', engine: 'AggregatingMergeTree',
    domain: 'player', category: 'gold',
    description: 'Daily unique player activity by type (casino bet, sport bet, deposit, login).',
    partitionBy: 'toYYYYMM(date)',
    orderBy: '(site_id, date, activity_type)',
    columns: [
      { name: 'site_id', type: 'UInt32', description: 'Site' },
      { name: 'date', type: 'Date', description: 'Business day' },
      { name: 'activity_type', type: 'LowCardinality(String)', description: 'Activity category' },
      { name: 'unique_players', type: 'AggregateFunction(uniq, UInt64)', description: 'Distinct active players' },
      { name: 'event_count', type: 'AggregateFunction(sum, UInt64)', description: 'Total events' },
    ],
  },
  {
    name: 'agg_rtp_daily', displayName: 'RTP Daily', engine: 'AggregatingMergeTree',
    domain: 'casino', category: 'gold',
    description: 'Daily RTP (Return to Player) by brand and game.',
    partitionBy: 'toYYYYMM(date)',
    orderBy: '(site_id, date, brand_id, game_id)',
    columns: [
      { name: 'site_id', type: 'UInt32', description: 'Site' },
      { name: 'date', type: 'Date', description: 'Business day' },
      { name: 'brand_id', type: 'UInt32', description: 'Brand' },
      { name: 'game_id', type: 'UInt32', description: 'Game' },
      { name: 'win_amount_real_*', type: 'AggregateFunction(sum)', description: 'Win amounts for RTP calc' },
      { name: 'turnover_real_*', type: 'AggregateFunction(sum)', description: 'Turnover for RTP calc' },
    ],
  },
  {
    name: 'agg_financial_kpi_daily', displayName: 'Financial KPI', engine: 'AggregatingMergeTree',
    domain: 'aggregation', category: 'gold',
    description: 'Master KPI table — ~120 columns covering all financial metrics per site per day.',
    partitionBy: 'toYYYYMM(date)',
    orderBy: '(site_id, date)',
    columns: [
      { name: 'site_id', type: 'UInt32', description: 'Site' },
      { name: 'date', type: 'Date', description: 'Business day' },
      { name: 'registrations', type: 'AggregateFunction(sum, UInt64)', description: 'New registrations' },
      { name: 'active_players', type: 'AggregateFunction(uniq, UInt64)', description: 'Unique active players' },
      { name: 'casino_ggr_*', type: 'AggregateFunction(sum)', description: 'Casino GGR (× currencies)' },
      { name: 'sport_ggr_*', type: 'AggregateFunction(sum)', description: 'Sport GGR' },
      { name: 'total_deposits_*', type: 'AggregateFunction(sum)', description: 'Total deposits' },
      { name: 'total_withdrawals_*', type: 'AggregateFunction(sum)', description: 'Total withdrawals' },
      { name: 'bonus_cost_*', type: 'AggregateFunction(sum)', description: 'Bonus costs' },
      { name: 'ngr_*', type: 'AggregateFunction(sum)', description: 'NGR = GGR - bonus costs' },
    ],
  },
  {
    name: 'agg_betstarters_kpi_daily', displayName: 'BetStarters KPI', engine: 'AggregatingMergeTree',
    domain: 'aggregation', category: 'gold',
    description: 'BetStarters-specific KPIs — site visitors, registrations, confirmed accounts.',
    partitionBy: 'toYYYYMM(date)',
    orderBy: '(site_id, date)',
    columns: [
      { name: 'site_id', type: 'UInt32', description: 'Site' },
      { name: 'date', type: 'Date', description: 'Business day' },
      { name: 'site_visitors', type: 'AggregateFunction(sum, UInt64)', description: 'Unique visitors' },
      { name: 'registrations', type: 'AggregateFunction(sum, UInt64)', description: 'Registrations' },
      { name: 'confirmed_registrations', type: 'AggregateFunction(sum, UInt64)', description: 'Email confirmed' },
      { name: 'unique_depositors', type: 'AggregateFunction(uniq, UInt64)', description: 'Unique depositors' },
      { name: 'deposits_count', type: 'AggregateFunction(sum, UInt64)', description: 'Deposit count' },
    ],
  },
];

// ── Helpers ──────────────────────────────────────────────────

export const getTablesByCategory = (category: TableSchema['category']) =>
  TABLES.filter((t) => t.category === category);

export const getEventsByDomain = (domain: EventType['domain']) =>
  EVENT_TYPES.filter((e) => e.domain === domain);

export const totalColumns = TABLES.reduce((sum, t) => sum + t.columns.length, 0);
